<template>
    <!-- BaseContainer.vue -->
    <v-container class="pa-0" fluid>
        <v-skeleton-loader
            :loading="loading"
            height="48"
            type="image"
        >
            <!-- Container Header -->
            <container-header
                :suffix-text="totalPagesDescription"
                :suffix-text-color="suffixTextColor"
                :title="title"
                :title-color="titleColor"
                class="mt-3 mb-1"
            ></container-header>
        </v-skeleton-loader>
        <!-- Container Loading -->
        <v-row v-if="loading" align="center" class="text-center" no-gutters style="height:100vh">
            <v-col cols="12">
                <v-progress-circular :size="200" :width="5" color="primary" indeterminate>
                    <span class="accent--text font-weight-medium text-h5">Loading...</span>
                </v-progress-circular>
            </v-col>
        </v-row>
        <!-- Container Content -->
        <template v-else>
            <!-- Progress -->
            <v-progress-linear
                :color="pageInterval ? 'primary' : 'transparent'"
                :value="pageIntervalTimeoutProgress"
            ></v-progress-linear>
            <!-- Tiles Container Grid -->
            <v-row :ref="gridRef" class="mt-0" dense style="height: calc(100vh - 80px); overflow: auto;">
                <template v-if="hasItemsSliced">
                    <!-- @slot item -->
                    <slot
                        v-for="item in itemsSliced"
                        :item="item"
                        :tile-height="tileHeight"
                        name="item"
                    ></slot>
                </template>
                <!-- @slot no-data -->
                <slot v-else :tile-height="tileHeight" name="no-data"></slot>
            </v-row>
        </template>
    </v-container>
</template>

<script>
    import {isEmpty, uniqueId} from 'underscore'
    import ContainerHeader from './ContainerHeader'

    export default {
        name: 'BaseContainer',
        extends: ContainerHeader,
        data: () => ({
            dimensions: null,
            dimensionsInterval: null,
            page: 1,
            pageInterval: null,
            pageIntervalTimeout: null,
            tileHeight: null
        }),
        props: {
            /**
             * An array of items to display i.e. [{color, title, value}]
             */
            items: {
                type: Array,
                required: true
            },
            /**
             * Toggle the skeleton loader
             */
            loading: {
                type: Boolean,
                default: false
            },
            /**
             * How long each page is displayed for, if there is more than one
             */
            pageTimeout: {
                type: Number,
                default: 30000
            },
            /**
             * The padding in pixels applied to tiles in the slot.  Default is `pa-2` which is 16px
             */
            tilePadding: {
                type: Number,
                default: 16
            }
        },
        components: {
            ContainerHeader
        },
        computed: {
            gridRef () {
                return uniqueId('container_grid_')
            },
            hasItemsSliced () {
                return !isEmpty(this.itemsSliced)
            },
            itemsSliced () {
                const vm = this
                const items = vm.items

                if (!vm.dimensions) {
                    return items
                }

                const totalVisible = vm.totalVisible
                const start = vm.page === 1 ? 0 : (totalVisible * (vm.page - 1))
                const end = totalVisible * vm.page

                return items.slice(start, end)
            },
            pageIntervalTimeoutProgress () {
                const vm = this
                return (vm.pageIntervalTimeout / vm.pageTimeout) * 100
            },
            totalPages () {
                const vm = this
                const totalVisible = vm.totalVisible

                return totalVisible
                    ? Math.ceil((vm.items.length) / totalVisible)
                    : null
            },
            totalPagesDescription () {
                const vm = this
                return vm.totalPages > 1 ? `${vm.page} / ${vm.totalPages}` : ''
            },
            totalVisible () {
                const vm = this
                return vm.dimensions && (vm.dimensions.yVisible * vm.dimensions.xVisible)
            }
        },
        methods: {
            calculateTileDimensions () {
                const vm = this
                let heights = []
                let widths = []
                let height = 0
                let width = 0

                // For each item, get the height and width of its tile
                vm.items.forEach((item) => {
                    const statsTile = document.getElementById(item.uuid)
                    if (statsTile) {
                        heights.push(statsTile.clientHeight + vm.tilePadding)
                        widths.push(statsTile.clientWidth + vm.tilePadding)
                    }
                })

                if (!isEmpty(heights)) {
                    height = Math.max(...heights)
                }

                if (!isEmpty(widths)) {
                    width = Math.max(...widths)
                }

                return {height, width}
            },
            calculateDimensions () {
                const vm = this

                vm.resetDimensions()

                vm.dimensionsInterval = setInterval(() => {
                    // Get the container grid by ref
                    const statsContainer = vm.$refs[vm.gridRef]

                    // Wait until stats container is mounted on DOM
                    if (statsContainer) {
                        // Store tile heights & widths to work out max values
                        let tileDimensions = vm.calculateTileDimensions()

                        // Calculate container and tile dimensions
                        const containerWidth = statsContainer.clientWidth
                        const containerHeight = statsContainer.clientHeight
                        const tileWidth = tileDimensions.width
                        let tileHeight = tileDimensions.height

                        // Calculate how many tiles to display in the x and y axis
                        let xVisible = containerWidth / tileWidth
                        let yVisible = containerHeight / tileHeight

                        // If visible is set to 0, then override to 1
                        xVisible = Math.round(xVisible === 0 ? 1 : xVisible)
                        yVisible = Math.ceil(yVisible === 0 ? 1 : yVisible)

                        // Set the dimensions object
                        vm.dimensions = {xVisible, yVisible}

                        // On next tick check if container is scrollable, and readjust y visibility
                        vm.$nextTick(() => {
                            if (statsContainer.clientHeight < statsContainer.scrollHeight) {
                                vm.dimensions.yVisible = Math.floor(containerHeight / tileHeight)
                            }

                            // On next tick increase tile size if there is white space
                            vm.$nextTick(() => {
                                tileDimensions = vm.calculateTileDimensions()
                                tileHeight = tileDimensions.height

                                const spaceRemaining = (statsContainer.clientHeight - vm.tilePadding) - (tileHeight * vm.dimensions.yVisible)
                                const tileBuffer = spaceRemaining / vm.totalVisible

                                vm.tileHeight = tileBuffer > 0
                                    ? tileHeight + tileBuffer
                                    : null
                            })
                        })

                        // Clear interval
                        vm.resetDimensionsInterval()
                    }
                }, 100)
            },
            resetDimensionsInterval () {
                const vm = this
                if (vm.dimensionsInterval) {
                    clearInterval(vm.dimensionsInterval)
                    vm.dimensionsInterval = null
                }
            },
            resetPageInterval () {
                const vm = this
                if (vm.pageInterval) {
                    clearInterval(vm.pageInterval)
                    vm.pageInterval = null
                    vm.page = 1
                }
            },
            resetDimensions () {
                const vm = this
                vm.tileHeight = null
                vm.dimensions = null

                vm.resetPageInterval()
                vm.resetDimensionsInterval()
            },
            startPageInterval () {
                const vm = this
                const timeout = 1000

                vm.resetPageInterval()
                vm.pageIntervalTimeout = vm.pageTimeout

                vm.pageInterval = setInterval(() => {
                    vm.pageIntervalTimeout -= timeout

                    if (vm.pageIntervalTimeout === 0) {
                        setTimeout(() => {
                            vm.pageIntervalTimeout = vm.pageTimeout

                            if (vm.page < vm.totalPages) {
                                return vm.page++
                            }

                            vm.page = 1
                        }, 250)
                    }
                }, timeout)
            }
        },
        watch: {
            items (newVal, oldVal) {
                const newLength = newVal && newVal.length
                const oldLength = oldVal && oldVal.length

                if (newLength !== oldLength) {
                    this.calculateDimensions()
                }
            },
            totalPages (newVal) {
                const vm = this

                vm.resetPageInterval()

                if (newVal > 1) {
                    vm.startPageInterval()
                }
            }
        },
        mounted () {
            const vm = this
            vm.calculateDimensions()
            window.addEventListener('resize', vm.calculateDimensions)
        },
        destroyed () {
            const vm = this
            vm.resetDimensions()
            window.removeEventListener('resize', vm.calculateDimensions)
        }
    }
</script>
